import React from 'react';
import { Link } from 'react-router-dom';
import { FixedSizeList } from 'react-window';
import { IDexBigTrade, ITicker } from '@kattana-io/common';
import { AutoSizer } from 'react-virtualized';
import BigNumber from 'bignumber.js';
import {
  useTable,
  useFlexLayout,
  useResizeColumns,
  useRowState,
} from 'react-table';
import Text from '../../atoms/Text';
import { useBigTradesTableData } from './useBigTradesTableData';
import TableDateField from '../../components/TableComponents/Date';
import { toFixed4 } from '../../components/DexTrades/formatters';
import TickerCell from '../../components/DexTrades/TickerCell';
import { formatUSD } from '../../components/Modal/TickersModal/helper';

interface IBigTradesTableProps {
  trades: IDexBigTrade[];
}

const denom = (input: string, decimals: number): string => {
  try {
    const denom = new BigNumber(10).pow(decimals);
    return new BigNumber(input).dividedBy(denom).toFixed(0);
  } catch (err) {
    console.warn(err);
    return '';
  }
};

const formatBaseAmount = (trade: any, ticker: ITicker | undefined): string => {
  if (ticker) {
    return denom(trade.amount0, ticker.base_decimals);
  }
  return trade.amount0;
};

const formatQuoteAmount = (trade: any, ticker: ITicker | undefined): string => {
  if (ticker) {
    return denom(trade.amount1, ticker.quote_decimals);
  }
  return trade.amount1;
};

const BigTradesTable = ({ trades }: IBigTradesTableProps) => {
  const { columns, data } = useBigTradesTableData({ trades });

  const tableInstance = useTable(
    { columns: columns, data },
    useFlexLayout,
    useResizeColumns,
    useRowState
  );

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
    tableInstance;

  const HeaderGroup = ({ headerGroup }: any) => (
    <div className='tr thr' {...headerGroup.getHeaderGroupProps()}>
      {headerGroup.headers.map((column: any) => (
        <div
          className='th'
          key={JSON.stringify(column)}
          {...column.getHeaderProps()}
        >
          <Text size={12} className='text--grey-600 text--weight-600'>
            {column.render('Header')}
          </Text>
        </div>
      ))}
    </div>
  );

  const RenderRow = React.useCallback(
    ({ index, style }) => {
      const row: any = rows[index];
      prepareRow(row);
      return (
        <Link
          to={`/${row.original.chain}/${row.original.pair}`}
          {...row.getRowProps()}
          style={{ ...style, display: 'flex', textDecoration: 'none' }}
        >
          {row.cells.map((cell: any) => (
            <div className='td' key={index} {...cell.getCellProps()}>
              {Cell(cell)}
            </div>
          ))}
        </Link>
      );
    },
    [prepareRow, rows]
  );

  const Cell = (cell: any): React.ReactNode => {
    const { chain, pair, ticker } = cell.row.original;
    const key = `${chain}/${pair}`;

    switch (cell.column.id) {
    case 'ticker':
      return <TickerCell path={key} ticker={ticker} />;
    case 'amount_base':
      return (
        <div className='text--12 text--grey-600'>
          {formatBaseAmount(cell.row.original, ticker)}
        </div>
      );
    case 'amount_quote':
      return (
        <div className='text--12 text--grey-600'>
          {formatQuoteAmount(cell.row.original, ticker)}
        </div>
      );
    case 'time':
      return (
        <TableDateField timestamp={new Date(cell.value).getTime() * 1000} />
      );
    case 'buy':
      const side = cell.value ? 'Buy' : 'Sell';
      return (
        <Text
          size={12}
          className={cell.value ? 'text--green-700' : 'text--red-700'}
        >
          {side}
        </Text>
      );
    case 'price_a_usd':
      return (
        <div className='text--12 text--grey-600'>{toFixed4(cell.value)}</div>
      );
    case 'wallet':
      return (
        <Text size={12} className='text--navy-700'>
          {cell.value.slice(0, 6)}...{cell.value.slice(cell.value.length - 4)}
        </Text>
      );
    case 'value_usd':
      return (
        <Text size={12} className='text--grey-600'>
          {formatUSD(cell.value)}
        </Text>
      );
    default:
      return <div className='text--12 text--grey-600'>{cell.value}</div>;
    }
  };

  return (
    <>
      {!data.length ? (
        <div className='tableCell centrify'>No data found</div>
      ) : (
        <div className='bigTrades__table' {...getTableProps()}>
          <div className='thead'>
            {headerGroups.map((headerGroup, index) => (
              <HeaderGroup key={index} headerGroup={headerGroup} />
            ))}
          </div>
          <div className='tbody' {...getTableBodyProps()}>
            <AutoSizer>
              {({ height, width }) => (
                <FixedSizeList
                  height={height}
                  itemCount={rows.length}
                  itemSize={40}
                  width={width}
                  className='tbody__autosizer'
                  style={{ overflow: 'overlay' }}
                >
                  {RenderRow}
                </FixedSizeList>
              )}
            </AutoSizer>
          </div>
        </div>
      )}
    </>
  );
};

export default BigTradesTable;
